import { Injectable } from '@angular/core';
import { SafeUrl } from "@angular/platform-browser";
import { Exercise } from "./../models/Exercises";
import { BackendService } from './backend.service';
import { Module, IModule } from 'app/shared/models/Module';
import { Program, IProgram } from 'app/shared/models/Program';
import { Category } from 'app/shared/models/Category'
import { environment } from 'environments/environment';
import { TranslateService } from '@ngx-translate/core';

export interface IExercise {

  _id: string;

  title: {
    internal: string,
    de: string,
    en: string
  };

  categories: Category[];
  categoryIDs: string[];

  isFavorite: boolean;

  previewUrl: SafeUrl | string;

  /*video: {
    de: Blob
  };*/
  videoSize: {
    de: number
  };
  videoUrl: {
    de: SafeUrl | string,
    en: SafeUrl | string
  };
  captionUrl: {
    de: SafeUrl | string,
    en: SafeUrl | string
  };
}

@Injectable()
export class ExerciseService {
  /** Array containing all Exercise instances */
  private allExercises: Exercise[] = []

  /** Array containing all Modules instances available for the user */
  private allModules: Module[] = []

  /** Array containing all user d Programs */
  private allMyPrograms: Program[] = []

  /** Array containing all main Categories */
  private allMainCategories: Category[] = []

  constructor(private backendService: BackendService, private translate: TranslateService) { }

  /** loads ALL user accessible Exercises from the DB. */
  private exercisePromise: Promise<Exercise[]>
  getAllExercises(): Promise<Exercise[]> {

    // No need to load again if we already downloaded all of them
    if (this.allExercises !== undefined && this.allExercises.length != 0) {
      // already loaded from server, use cached data
      return Promise.resolve(this.allExercises)
    } else if (this.exercisePromise){ 
      // not laoded, but the same request already went out
      return this.exercisePromise.then(exercises => {
        this.allExercises = exercises as IExercise[]
        return this.allExercises
      })
    } 
    
    // not loaded, not loading, initiate api call
    var tasks = [
      this.getMainCategories(),
      this.backendService.get("exercise/all/")
    ]

    this.exercisePromise = Promise.all(tasks).then(all => {
      let allCategories = all[0] as Category[]
      let exercises = all[1]
      this.allExercises = (exercises as IExercise[]).map(ex => {
        ex.previewUrl = `${environment.backendUrl}exercise/image/${ex._id}.jpeg`
        ex.videoUrl = {
          de: `${environment.backendUrl}exercise/video/de/${ex._id}.mp4`,
          en: `${environment.backendUrl}exercise/video/en/${ex._id}.mp4`
        }
        ex.captionUrl = {
          de: `${environment.backendUrl}exercise/caption/de/${ex._id}.vtt`,
          en: `${environment.backendUrl}exercise/caption/en/${ex._id}.vtt`
        }
        ex.categories = []
        ex.categoryIDs.forEach((item, index) => {
          let category = allCategories.find(cat => cat._id === item)
          if (category) {
            ex.categories.push(category)
          }
        })
        return ex
      })

      this.exercisePromise = undefined
      return this.allExercises as IExercise[]
    }).catch( error => {
      this.exercisePromise = undefined
      console.log(error)
      return []
    })
    return this.exercisePromise
  }

  /** should return a list of modules and their respective classes */
  private modulePromise: Promise<Module[]>
  getModules(): Promise<Module[]>{
    // No need to load again if we already downloaded all of them
    if (this.allModules !== undefined && this.allModules.length != 0) {
      // already loaded from server, use cached data
      return Promise.resolve(this.allModules)
    } else if (this.modulePromise){ 
      // not laoded, but the same request already went out
      return this.modulePromise.then(modules => {
        this.allModules = modules as Module[]
        return this.allModules
      })
    } 

    this.modulePromise = this.backendService.get("modules/").then(data => {
      this.allModules = []

      var exTasks = []

      // get associated exercises by id
      for(let m of data as IModule[]){
        let module = Module.fromJson(m)
        this.allModules.push(module)
        exTasks.push(this.getExercises(module.exerciseIds))
      }

      return Promise.all(exTasks)
    }).then(all => {
      all.forEach((item, index) => {
        this.allModules[index].exercises = item
      });

      this.modulePromise = undefined

      // set preview url
      this.allModules.map(item => {
        item.previewUrl = `${environment.backendUrl}module/image/${item._id}.jpeg`
      })

      return this.allModules
    }).catch(error => {
      console.log(error);
      return [];
    })

    return this.modulePromise
  }

  getExercise(id: string): Promise<Exercise> {
    return this.getAllExercises().then(exs => {
      return exs.filter(ex => ex._id == id)[0]
    })
  }

  private getExercises(ids: string[]): Promise<Exercise[]> {
    var tasks = []
    
    for(let id of ids) {
      tasks.push(this.getExercise(id))
    }

    return Promise.all(tasks)
  }

  /** s a random Exercise Program with programLength number of Exercises from the given category and module */
  private randomExerciseIdHistory: string[] = []
  createRandomProgram(module: Module, category: Category): Promise<Program> {
    let randomProgram = new Program();
    randomProgram.isRandom = true
    randomProgram.title = module.title[this.translate.currentLang];
    randomProgram.exerciseIds = []
    randomProgram.exercises = []
    randomProgram.contextID = module._id;

    var possibleExercises = []
    let pSize = 0;
    if (category) {
      // when a category is specified we filter out every exercise that hat this category
      possibleExercises = module.exercises.filter(ex => {
        // exercise has category
        return ex.categoryIDs.find(cat => cat == category._id) != undefined
      })
      pSize = Math.min((module.randomProgramSize || 1), possibleExercises.length)

    } else {
      // no category specified so we just take all
      possibleExercises = module.exercises
      pSize = Math.min((module.randomProgramSize || 1), module.exercises.length)
    }
    
    // filter exercises that have already been viewed
    possibleExercises = possibleExercises.filter(ex => this.randomExerciseIdHistory.find(id => id == ex._id) == undefined)    


    // in case we can't find an exercise like this, reset history and try again
    if (possibleExercises.length < pSize) {
      this.randomExerciseIdHistory = []
      console.log("reset view history")

      return this.createRandomProgram(module, category)
    }

    //  as many random programs as necessary and possible
    for(let i = 0; i < pSize && i < possibleExercises.length;i++) {
      // find random exercise
      var index = Math.ceil(Math.random() * possibleExercises.length) - 1;
      var ex = possibleExercises[index]
      // remove from list so we dont get doubles
      possibleExercises.splice(index,1)
      // add to program
      randomProgram.exercises.push(ex)
      this.randomExerciseIdHistory.push(ex._id)
    }


    for(let ex of randomProgram.exercises){
      randomProgram.exerciseIds.push(ex._id)
    }
    console.log(randomProgram.exerciseIds)

    // resolve exercise IDs
    return Promise.resolve(randomProgram);
  }

  getMyPrograms(): Promise<Program[]> {
    // No need to load again if we already downloaded all of them
    if (this.allMyPrograms !== undefined && this.allMyPrograms.length != 0) {
      // already loaded from server, use cached data
      return Promise.resolve(this.allMyPrograms)
    }

    // load from "server"
    /* Loads my programs and associated exercises from server */
    return this.backendService.get("programs").then(data => {
      this.allMyPrograms = []
      var tasks = []

      // get associated exercises by id
      for(let p of data as IProgram[]){
        let program = Program.fromJson(p)
        this.allMyPrograms.push(program)
        tasks.push(this.getExercises(program.exerciseIds))
      }

      return Promise.all(tasks).then(all => {
        all.forEach((item, index) => {
          this.allMyPrograms[index].exercises = item

          this.allMyPrograms[index].previewUrl = ""
          if (this.allMyPrograms[index].exercises.length != 0){
            this.allMyPrograms[index].previewUrl = this.allMyPrograms[index].exercises[0].previewUrl
          }
        });
        

        return this.allMyPrograms
      })
    })
  }


  saveNewProgram(program: Program): Promise<any> {
    return this.backendService.post(`programs`, program).then(result => {
      program._id = (result as IProgram)._id
      this.allMyPrograms.splice(0,0,program)
      return result
    })
  }

  updateMyProgram(program: Program): Promise<any> {
    return this.backendService.put(`programs/${program._id}/`, program).then(result => {
      for(let p of this.allMyPrograms){
        if (p._id == program._id) { 
          p = program;
        }
      }
      return result
    })
  }

  deleteMyProgram(program: Program): Promise<any> {
    return this.backendService.delete_(`programs/${program._id}/`).then(result => {
      this.allMyPrograms.splice(this.allMyPrograms.indexOf(program),1)
      
      return result
    })
  }

  private mainCategoryPromise: Promise<Category[]>
  getMainCategories(): Promise<Category[]> {
    // No need to load again if we already downloaded all of them
    if (this.allMainCategories !== undefined && this.allMainCategories.length != 0) {
      // already loaded from server, use cached data
      return Promise.resolve(this.allMainCategories)
    } else if (this.mainCategoryPromise){ 
      // not laoded, but the same request already went out
      return this.mainCategoryPromise.then(categories => {
        this.allMainCategories = categories as Category[]
        return this.allMainCategories
      })
    } 

    // load from server
    this.mainCategoryPromise = this.backendService.get("category/main").then(categories => {
      this.allMainCategories = categories as Category[]

      this.mainCategoryPromise = undefined
      return this.allMainCategories;
    }).catch( error => {
      this.mainCategoryPromise = undefined
      console.log(error)
      return []
    })
    return this.mainCategoryPromise
  }


  /**
   * Called when user finishes a module to trigger team challenge bonus point calculation in backend
   */
   public moduleFinished(moduleID: string) {
    return this.backendService.post(`modules/${moduleID}/watched`).then().catch();
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src1521087412/src/client/web-client/src/app/shared/services/exercises.service.ts